class HeroCarousel {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.cardsWrapper = document.getElementById(`CardsCarousel-${sectionId}`);
    this.cardsTrack = this.cardsWrapper?.querySelector('.hero-carousel__cards-track');
    this.cardsContainer = this.cardsWrapper?.closest('.hero-carousel__cards-container');
    // Use hero navigation buttons to control cards
    this.heroSection = this.cardsWrapper?.closest('.hero-carousel');
    this.prevButton = this.heroSection?.querySelector(`[data-carousel-prev]`);
    this.nextButton = this.heroSection?.querySelector(`[data-carousel-next]`);

    console.log('Button selectors found:', {
      heroSection: !!this.heroSection,
      prevButton: !!this.prevButton,
      nextButton: !!this.nextButton,
      prevButtonElement: this.prevButton,
      nextButtonElement: this.nextButton,
    });

    if (!this.cardsTrack || !this.prevButton || !this.nextButton) {
      console.warn('HeroCarousel: Required elements not found', {
        cardsTrack: !!this.cardsTrack,
        prevButton: !!this.prevButton,
        nextButton: !!this.nextButton,
        sectionId: this.sectionId,
      });
      return;
    }

    // Mobile uses native CSS scroll-snap on .hero-carousel__cards-wrapper
    // (see section-hero-carousel.css @ max-width: 749px). Binding the
    // JS-driven transform/swipe path there double-handles the gesture and
    // — because the touchmove listener calls preventDefault() — blocks
    // the native horizontal scroll entirely. Defer to CSS on mobile.
    this.mobileBreakpoint = 749;
    this.isMobile = window.matchMedia(`(max-width: ${this.mobileBreakpoint}px)`).matches;

    this.currentIndex = 0;
    this.cards = this.cardsTrack.querySelectorAll('.hero-carousel__card');
    this.totalCards = this.cards.length;
    this.cardsPerView = this.getCardsPerView();
    this.maxIndex = Math.max(0, this.totalCards - Math.floor(this.cardsPerView));

    // Reverse card order for RTL
    this.reverseCardsForRTL();

    this.init();
  }

  reverseCardsForRTL() {
    const isRTL = document.documentElement.dir === 'rtl' || document.documentElement.getAttribute('lang') === 'ar';
    if (isRTL) {
      // Reverse the order of cards in the DOM for RTL
      const cardsArray = Array.from(this.cards);
      cardsArray.reverse();
      cardsArray.forEach((card) => this.cardsTrack.appendChild(card));

      // Update cards reference
      this.cards = this.cardsTrack.querySelectorAll('.hero-carousel__card');
    }
  }

  getCardsPerView() {
    // Responsive card widths based on screen size
    let cardWidth;
    if (window.innerWidth >= 1024) {
      cardWidth = 380; // Desktop
    } else if (window.innerWidth >= 750) {
      cardWidth = 320; // Tablet
    } else if (window.innerWidth >= 480) {
      cardWidth = 280; // Mobile
    } else {
      cardWidth = 240; // Small mobile
    }

    const containerWidth = this.cardsContainer.offsetWidth;
    return containerWidth / cardWidth; // Calculate how many cards fit in the container
  }

  init() {
    if (this.isMobile) {
      // Native CSS scroll handles gestures + slide tracking on mobile;
      // skip JS transform updates and touch bindings entirely. Resize
      // listener still rebinds if the viewport crosses the breakpoint.
      this.handleResize();
      return;
    }
    this.updateCarousel();
    this.bindEvents();
    this.handleResize();
  }

  bindEvents() {
    this.nextButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.nextSlide();
    });

    this.prevButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.prevSlide();
    });

    this.nextButton.addEventListener('touchend', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.nextSlide();
    });

    this.prevButton.addEventListener('touchend', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.prevSlide();
    });

    this.addTouchSupport();
    this.addKeyboardSupport();
  }

  addTouchSupport() {
    let startX = 0;
    let startY = 0;
    let isScrolling = false;
    let touchStartTime = 0;

    this.cardsWrapper.addEventListener(
      'touchstart',
      (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        touchStartTime = Date.now();
        isScrolling = false;
      },
      { passive: true }
    );

    this.cardsWrapper.addEventListener(
      'touchmove',
      (e) => {
        if (!startX || !startY) return;

        const diffX = startX - e.touches[0].clientX;
        const diffY = startY - e.touches[0].clientY;

        // Only prevent default if we're definitely swiping horizontally
        if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 10) {
          isScrolling = true;
          e.preventDefault();
        }
      },
      { passive: false }
    );

    this.cardsWrapper.addEventListener(
      'touchend',
      (e) => {
        if (!isScrolling || !startX || !startY) {
          startX = 0;
          startY = 0;
          isScrolling = false;
          return;
        }

        const endX = e.changedTouches[0].clientX;
        const diffX = startX - endX;
        const touchDuration = Date.now() - touchStartTime;

        // Require minimum swipe distance and maximum touch duration for swipe
        if (Math.abs(diffX) > 50 && touchDuration < 500) {
          const isRTL =
            document.documentElement.dir === 'rtl' || document.documentElement.getAttribute('lang') === 'ar';

          if (isRTL) {
            // RTL: Right swipe (diffX < 0) should go to next slide, left swipe (diffX > 0) should go to previous
            if (diffX < 0) {
              this.nextSlide(); // Right swipe goes to next
            } else {
              this.prevSlide(); // Left swipe goes to previous
            }
          } else {
            // LTR: Left swipe (diffX > 0) should go to next slide, right swipe (diffX < 0) should go to previous
            if (diffX > 0) {
              this.nextSlide(); // Left swipe goes to next
            } else {
              this.prevSlide(); // Right swipe goes to previous
            }
          }
        }

        startX = 0;
        startY = 0;
        isScrolling = false;
      },
      { passive: true }
    );
  }

  addKeyboardSupport() {
    this.cardsWrapper.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.prevSlide();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.nextSlide();
      }
    });

    // Make the carousel focusable
    this.cardsWrapper.setAttribute('tabindex', '0');
  }

  updateCarousel() {
    if (this.isMobile) return;
    const isRTL = document.documentElement.dir === 'rtl' || document.documentElement.getAttribute('lang') === 'ar';
    let translateX = -this.currentIndex * (100 / this.cardsPerView);

    if (isRTL) {
      translateX = -translateX;
    }

    this.cardsTrack.style.transform = `translateX(${translateX}%)`;

    this.prevButton.disabled = this.currentIndex === 0;
    this.nextButton.disabled = this.currentIndex >= this.maxIndex;
    this.prevButton.removeAttribute('disabled');
    this.nextButton.removeAttribute('disabled');

    this.updateAriaAttributes();
  }

  updateAriaAttributes() {
    this.cardsTrack.setAttribute('aria-live', 'polite');
    this.cardsTrack.setAttribute(
      'aria-label',
      `Showing cards ${this.currentIndex + 1} to ${Math.min(
        this.currentIndex + this.cardsPerView,
        this.totalCards
      )} of ${this.totalCards}`
    );
  }

  nextSlide() {
    if (this.currentIndex < this.maxIndex) {
      this.currentIndex++;
      this.updateCarousel();
    }
  }

  prevSlide() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateCarousel();
    }
  }

  handleResize() {
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const wasMobile = this.isMobile;
        this.isMobile = window.matchMedia(`(max-width: ${this.mobileBreakpoint}px)`).matches;

        // Crossing the breakpoint: clear any leftover JS transform when
        // entering mobile so the native scroll has a clean track, and
        // recompute desktop indices when entering desktop.
        if (wasMobile && !this.isMobile) {
          this.cardsPerView = this.getCardsPerView();
          this.maxIndex = Math.max(0, this.totalCards - Math.floor(this.cardsPerView));
          this.updateCarousel();
          this.bindEvents();
        } else if (!wasMobile && this.isMobile) {
          this.cardsTrack.style.transform = '';
        }

        if (!this.isMobile) {
          const newCardsPerView = this.getCardsPerView();
          const newMaxIndex = Math.max(0, this.totalCards - newCardsPerView);

          if (this.currentIndex > newMaxIndex) {
            this.currentIndex = newMaxIndex;
          }

          this.cardsPerView = newCardsPerView;
          this.maxIndex = newMaxIndex;
          this.updateCarousel();
        }
      }, 250);
    });
  }
}

document.addEventListener('DOMContentLoaded', function () {
  const heroCarousels = document.querySelectorAll('[id^="CardsCarousel-"]');
  heroCarousels.forEach((carousel) => {
    const sectionId = carousel.id.replace('CardsCarousel-', '');
    new HeroCarousel(sectionId);
    carousel.dataset.initialized = 'true';
  });
});

// Export for potential use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = HeroCarousel;
}
